import { useMemo } from 'react'

import { useLocation, useNavigate } from 'react-router-dom'

import { convertParamsToTags } from '@components/music/filter/Tags/utils/helpers'
import { SUBFACETS } from '@components/music/filter/utils/constants'
import { getAggregationCategory, isSlugFilter } from '@components/music/filter/utils/helpers'
import useUpdateQueryParams, { BeforeNavigate } from '@hooks/useUpdateQueryParams'
import { usePageApi } from '@providers/PageApiProvider'
import { SearchPageApiResponse } from '@providers/types/api-responses'
import { emptyParamsRedirectTo } from '@utils/client'
import { removeTrailing } from '@utils/formatters/string.formatter'
import PageRoutes from '@utils/page-routes'

/**
 * Component-level business logic hook used for the `<Tags />` component
 */
const useTags = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { pageData } = usePageApi<SearchPageApiResponse>()

  const { deleteParams, params } = useUpdateQueryParams({
    beforeNavigate: onBeforeNavigate(location.pathname),
  })

  const tags = useMemo(
    () => convertParamsToTags(params, pageData?.slug_filter),
    [pageData?.slug_filter, params]
  )

  const hasTags = useMemo(() => Object.keys(tags).length > 0, [tags])

  const removeFilter = (category, filter): void => {
    const subFacetCategory = SUBFACETS[getAggregationCategory(category)]

    if (subFacetCategory && subFacetCategory[filter]) {
      deleteParams({ key: category, value: filter }, { key: subFacetCategory[filter], value: null })
    } else {
      // If filter is from slug, navigate to /music/search
      if (isSlugFilter(category, filter, pageData?.slug_filter)) {
        navigate(PageRoutes.musicSearchPath(location.search))
      }
      // Otherwise, remove the query param
      else {
        deleteParams({ key: category, value: filter })
      }
    }
  }

  return { tags, hasTags, removeFilter }
}

export default useTags

function onBeforeNavigate(pathname: string): BeforeNavigate {
  if (removeTrailing('/', pathname) === PageRoutes.musicSearchPath())
    return emptyParamsRedirectTo(PageRoutes.musicPath())
  return null
}
